import { Fragment, ReactNode, useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { Trash } from 'iconsax-react';
import type {
  CatalogueField,
  Condition,
  ConditionGroup,
  IntegrationLookups,
  Operator,
  Quantifier,
  SourceEntity,
  ValidationIssue
} from '../types';
import { entityNouns } from '../components/format';

/* ------------------------------ shared editor helpers ------------------------------ */

export const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** [singular, plural] noun for a record type — "load" / "loads", "record" / "records" for a table we have no name for. */
export const entityNoun = (e: SourceEntity | undefined): [string, string] => entityNouns(e ?? 'loads');

export const errorMessage = (e: unknown, fallback: string): string =>
  (typeof e === 'string' ? e : (e as { message?: string } | null)?.message) || fallback;

/** Validation issues whose path sits under one of the prefixes ("filter", "filter.children[0]" …). */
export const issuesAt = (issues: ValidationIssue[] | undefined, prefixes: string[]): ValidationIssue[] =>
  (issues ?? []).filter((i) => prefixes.some((p) => i.path === p || i.path.startsWith(`${p}.`) || i.path.startsWith(`${p}[`)));

export function IssueList({ issues }: { issues: ValidationIssue[] }) {
  if (!issues.length) return null;
  return (
    <Stack spacing={0.75}>
      {issues.map((i, k) => (
        <Alert key={k} severity={i.level === 'error' ? 'error' : 'warning'} sx={{ py: 0.25 }}>
          {i.message}
        </Alert>
      ))}
    </Stack>
  );
}

export const Mono = ({ children }: { children: ReactNode }) => (
  <Box component="code" sx={{ fontFamily: MONO, fontSize: '0.92em', color: 'text.primary' }}>
    {children}
  </Box>
);

/* ------------------------------ condition vocabulary ------------------------------ */

export const OPERATORS: Array<{ value: Operator; label: string }> = [
  { value: 'eq', label: 'is' },
  { value: 'neq', label: 'is not' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'in', label: 'is one of' },
  { value: 'not_in', label: 'is not one of' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: 'gt', label: 'is greater than' },
  { value: 'gte', label: 'is at least' },
  { value: 'lt', label: 'is less than' },
  { value: 'lte', label: 'is at most' },
  { value: 'exists_in_resources', label: 'is a known record in' }
];
const OP_LABEL = Object.fromEntries(OPERATORS.map((o) => [o.value, o.label])) as Record<Operator, string>;
const NO_VALUE: Operator[] = ['is_empty', 'is_not_empty'];

/** Repeating-group item keys a stop group's conditions read. */
export const STOP_ITEM_FIELDS: Array<{ value: string; label: string; code: string; hint?: string }> = [
  { value: 'type', label: 'Stop · type', code: 'ps{n}', hint: 'P (pickup) or S (delivery)' },
  { value: 'company', label: 'Stop · company', code: 'ps_company_{n}' },
  { value: 'address', label: 'Stop · address', code: 'ps_address_{n}' },
  { value: 'appt_date', label: 'Stop · appointment date', code: 'ps_appt_date_{n}' },
  { value: 'appt_time', label: 'Stop · appointment time', code: 'ps_appt_time_{n}' }
];

const QUANT_MODES: Array<{ value: Quantifier['mode']; label: string }> = [
  { value: 'any', label: 'At least one' },
  { value: 'all', label: 'Every' },
  { value: 'none', label: 'No' }
];

type Node = Condition | Quantifier | ConditionGroup;

export const newCondition = (field = ''): Condition => ({ kind: 'condition', field, operator: 'eq', value: '' });
export const newGroup = (itemMode = false): ConditionGroup => ({
  kind: 'group',
  op: 'AND',
  children: [newCondition(itemMode ? 'type' : '')]
});
export const newQuantifier = (): Quantifier => ({
  kind: 'quantifier',
  mode: 'any',
  group: 'extra_stops',
  skipFirst: true,
  where: { kind: 'group', op: 'AND', children: [newCondition('type')] }
});

/** Leaf conditions, with a stop group counted as one condition (matches how the filter reads). */
export const countConditions = (g: ConditionGroup | undefined | null): number =>
  (g?.children ?? []).reduce((n, c) => n + (c.kind === 'group' ? countConditions(c) : 1), 0);

/* ------------------------------ plain-sentence summary ------------------------------ */

interface DescribeCtx {
  lookups?: IntegrationLookups;
  itemMode?: boolean;
}

function describeCondition(c: Condition, ctx: DescribeCtx): ReactNode {
  const field = ctx.itemMode ? (
    <b>{(STOP_ITEM_FIELDS.find((f) => f.value === c.field)?.label ?? c.field).replace('Stop · ', '')}</b>
  ) : (
    <Mono>{c.field || '(no field)'}</Mono>
  );
  const op = OP_LABEL[c.operator] ?? c.operator;
  let value: ReactNode = null;
  if (c.operator === 'exists_in_resources') {
    const rt = ctx.lookups?.resourceTypes.find((r) => r._id === c.resourceTypeId);
    value = <b>{rt?.name ?? 'a resource list (not chosen)'}</b>;
  } else if (!NO_VALUE.includes(c.operator)) {
    value = <b>“{c.value ?? ''}”</b>;
  }
  return (
    <>
      {field} {op}
      {value ? <> {value}</> : null}
    </>
  );
}

function describeNode(n: Node, ctx: DescribeCtx): ReactNode {
  if (n.kind === 'condition') return describeCondition(n, ctx);
  if (n.kind === 'group') return describeGroup(n, ctx, true);
  const lead = n.mode === 'any' ? 'at least one' : n.mode === 'all' ? 'every' : 'no';
  return (
    <>
      <b>
        {lead}
        {n.skipFirst ? ' later' : ''} extra stop
      </b>{' '}
      where {describeGroup(n.where, { ...ctx, itemMode: true }, true)}
    </>
  );
}

function describeGroup(g: ConditionGroup, ctx: DescribeCtx, nested: boolean): ReactNode {
  const parts = g.children.map((c, i) => (
    <Fragment key={i}>
      {i > 0 ? (g.op === 'AND' ? ' and ' : ' or ') : null}
      {describeNode(c, ctx)}
    </Fragment>
  ));
  return nested && g.children.length > 1 ? <>({parts})</> : <>{parts}</>;
}

/** "Loads where ps1 is “P” and …" built from the tree. */
export function describeFilter(filter: ConditionGroup | undefined, plural: string, lookups?: IntegrationLookups): ReactNode {
  const cap = plural.charAt(0).toUpperCase() + plural.slice(1);
  if (!filter || !filter.children.length) return <>Every {plural.replace(/s$/, '')} matches — add a condition to narrow it down.</>;
  return (
    <>
      {cap} where {filter.op === 'OR' && filter.children.length > 1 ? <b>any one</b> : null}
      {filter.op === 'OR' && filter.children.length > 1 ? ' of these holds: ' : null}
      {describeGroup(filter, { lookups }, false)}.
    </>
  );
}

/* ------------------------------ builder UI ------------------------------ */

interface BuilderProps {
  group: ConditionGroup;
  onChange: (g: ConditionGroup) => void;
  catalogue: CatalogueField[];
  lookups: IntegrationLookups;
  /** Inside a stop group: fields are stop item keys, and stop groups can't nest. */
  itemMode?: boolean;
  root?: boolean;
}

export default function ConditionBuilder({ group, onChange, catalogue, lookups, itemMode = false, root = true }: BuilderProps) {
  const setChild = (i: number, next: Node) => onChange({ ...group, children: group.children.map((c, j) => (j === i ? next : c)) });
  const removeChild = (i: number) => onChange({ ...group, children: group.children.filter((_, j) => j !== i) });
  const add = (n: Node) => onChange({ ...group, children: [...group.children, n] });

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary', fontSize: 13 }}>
        <span>Match</span>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={group.op}
          onChange={(_, v) => v && onChange({ ...group, op: v })}
          aria-label="Match all or any conditions"
        >
          <ToggleButton value="AND" sx={{ py: 0.25, px: 1.5, textTransform: 'none' }}>
            all
          </ToggleButton>
          <ToggleButton value="OR" sx={{ py: 0.25, px: 1.5, textTransform: 'none' }}>
            any
          </ToggleButton>
        </ToggleButtonGroup>
        <span>of these conditions</span>
      </Stack>

      {group.children.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No conditions yet{root ? ' — every record matches.' : '.'}
        </Typography>
      )}

      {group.children.map((child, i) => {
        if (child.kind === 'condition') {
          return (
            <ConditionRow
              key={i}
              cond={child}
              itemMode={itemMode}
              catalogue={catalogue}
              lookups={lookups}
              onChange={(c) => setChild(i, c)}
              onRemove={() => removeChild(i)}
            />
          );
        }
        if (child.kind === 'group') {
          return (
            <Box key={i} sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: '10px', p: 1.25 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Group
                </Typography>
                <Tooltip title="Remove this group">
                  <IconButton size="small" aria-label="Remove this group" onClick={() => removeChild(i)}>
                    <Trash size={16} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <ConditionBuilder
                group={child}
                onChange={(g) => setChild(i, g)}
                catalogue={catalogue}
                lookups={lookups}
                itemMode={itemMode}
                root={false}
              />
            </Box>
          );
        }
        return (
          <QuantifierBox
            key={i}
            q={child}
            catalogue={catalogue}
            lookups={lookups}
            onChange={(q) => setChild(i, q)}
            onRemove={() => removeChild(i)}
          />
        );
      })}

      <Stack direction="row" spacing={0.75} flexWrap="wrap">
        <Button size="small" onClick={() => add(newCondition(itemMode ? 'type' : ''))}>
          + Condition
        </Button>
        <Button size="small" onClick={() => add(newGroup(itemMode))}>
          + Group
        </Button>
        {!itemMode && (
          <Button size="small" onClick={() => add(newQuantifier())}>
            + Stop group
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function QuantifierBox({
  q,
  onChange,
  onRemove,
  catalogue,
  lookups
}: {
  q: Quantifier;
  onChange: (q: Quantifier) => void;
  onRemove: () => void;
  catalogue: CatalogueField[];
  lookups: IntegrationLookups;
}) {
  return (
    <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: '10px', p: 1.25 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Select
          size="small"
          value={q.mode}
          onChange={(e) => onChange({ ...q, mode: e.target.value as Quantifier['mode'] })}
          inputProps={{ 'aria-label': 'How many stops' }}
        >
          {QUANT_MODES.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="body2">{q.skipFirst ? 'later ' : ''}extra stop where</Typography>
        <FormControlLabel
          sx={{ ml: 0.5 }}
          control={<Checkbox size="small" checked={!!q.skipFirst} onChange={(e) => onChange({ ...q, skipFirst: e.target.checked })} />}
          label={<Typography variant="caption">Later stops only (ignore extra stop 1)</Typography>}
        />
        <Box flex={1} />
        <Tooltip title="Remove this stop group">
          <IconButton size="small" aria-label="Remove this stop group" onClick={onRemove}>
            <Trash size={16} />
          </IconButton>
        </Tooltip>
      </Stack>
      <ConditionBuilder
        group={q.where}
        onChange={(where) => onChange({ ...q, where })}
        catalogue={catalogue}
        lookups={lookups}
        itemMode
        root={false}
      />
    </Box>
  );
}

function FieldPicker({ value, onChange, catalogue }: { value: string; onChange: (v: string) => void; catalogue: CatalogueField[] }) {
  const options = useMemo(() => catalogue.map((c) => c.field), [catalogue]);
  const byField = useMemo(() => new Map(catalogue.map((c) => [c.field, c])), [catalogue]);
  return (
    <Autocomplete
      freeSolo
      autoSelect
      size="small"
      options={options}
      value={value || null}
      onChange={(_, v) => onChange(typeof v === 'string' ? v.trim() : '')}
      renderOption={(props, option) => {
        const m = byField.get(option);
        return (
          <li {...props} key={option}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontFamily: MONO, fontSize: 13 }}>{option}</Box>
              <Typography noWrap sx={{ fontSize: 11.5, color: 'text.secondary', maxWidth: 360 }}>
                {m ? `${Math.round(m.fillPct)}% filled` : ''}
                {m?.samples?.[0] ? ` · e.g. ${m.samples[0]}` : ''}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Choose a field"
          inputProps={{ ...params.inputProps, 'aria-label': 'Field', style: { fontFamily: MONO, fontSize: 13 } }}
        />
      )}
    />
  );
}

function ConditionRow({
  cond,
  onChange,
  onRemove,
  catalogue,
  lookups,
  itemMode
}: {
  cond: Condition;
  onChange: (c: Condition) => void;
  onRemove: () => void;
  catalogue: CatalogueField[];
  lookups: IntegrationLookups;
  itemMode: boolean;
}) {
  const set = (patch: Partial<Condition>) => onChange({ ...cond, ...patch });
  const meta = itemMode ? undefined : catalogue.find((f) => f.field === cond.field);
  const item = itemMode ? STOP_ITEM_FIELDS.find((f) => f.value === cond.field) : undefined;
  const needsValue = !NO_VALUE.includes(cond.operator);
  const isResource = cond.operator === 'exists_in_resources';
  const isList = cond.operator === 'in' || cond.operator === 'not_in';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(170px,1.5fr) minmax(130px,1fr) minmax(140px,1.2fr) 34px',
        gap: 1,
        alignItems: 'start',
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '9px',
        bgcolor: 'grey.50'
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {itemMode ? (
          <Select
            fullWidth
            size="small"
            value={cond.field}
            onChange={(e) => set({ field: e.target.value })}
            inputProps={{ 'aria-label': 'Stop field' }}
            renderValue={(v) => STOP_ITEM_FIELDS.find((f) => f.value === v)?.label ?? v}
          >
            {STOP_ITEM_FIELDS.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                <Stack>
                  <span>{f.label}</span>
                  <Box component="code" sx={{ fontFamily: MONO, fontSize: 11, color: 'text.secondary' }}>
                    {f.code}
                  </Box>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        ) : (
          <FieldPicker value={cond.field} onChange={(field) => set({ field })} catalogue={catalogue} />
        )}
        {(meta || item) && (
          <Typography noWrap sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>
            {item ? (
              <>
                <Box component="code" sx={{ fontFamily: MONO }}>
                  {item.code}
                </Box>
                {item.hint ? ` · ${item.hint}` : ''}
              </>
            ) : meta ? (
              `${Math.round(meta.fillPct)}% filled${meta.samples?.[0] ? ` · e.g. ${meta.samples[0]}` : ''}`
            ) : null}
          </Typography>
        )}
      </Box>

      <Select
        size="small"
        value={cond.operator}
        onChange={(e) => {
          const operator = e.target.value as Operator;
          set(
            operator === 'exists_in_resources' ? { operator, value: undefined } : { operator, resourceTypeId: undefined, normalise: null }
          );
        }}
        inputProps={{ 'aria-label': 'Operator' }}
      >
        {OPERATORS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>

      <Box sx={{ minWidth: 0 }}>
        {isResource ? (
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={cond.resourceTypeId ?? ''}
            onChange={(e) => set({ resourceTypeId: e.target.value || undefined })}
            inputProps={{ 'aria-label': 'Resource list' }}
          >
            <MenuItem value="">
              <em>Choose a resource list</em>
            </MenuItem>
            {lookups.resourceTypes.map((rt) => (
              <MenuItem key={rt._id} value={rt._id}>
                {rt.name}
              </MenuItem>
            ))}
          </Select>
        ) : needsValue ? (
          <TextField
            fullWidth
            size="small"
            value={cond.value ?? ''}
            placeholder={isList ? 'Comma-separated values' : 'Value'}
            onChange={(e) => set({ value: e.target.value })}
            inputProps={{ 'aria-label': 'Value', style: { fontFamily: MONO, fontSize: 13 } }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            No value needed
          </Typography>
        )}
      </Box>

      <Tooltip title="Remove condition">
        <IconButton size="small" aria-label="Remove condition" onClick={onRemove} sx={{ mt: 0.5 }}>
          <Trash size={16} />
        </IconButton>
      </Tooltip>

      {isResource && (
        <FormControlLabel
          sx={{ gridColumn: '1 / -1', ml: 0.25 }}
          control={
            <Checkbox
              size="small"
              checked={cond.normalise === 'unpad_numeric_suffix'}
              onChange={(e) => set({ normalise: e.target.checked ? 'unpad_numeric_suffix' : null })}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              Ignore leading zeros, so <Mono>IAD85</Mono> matches <Mono>IAD085</Mono>
            </Typography>
          }
        />
      )}
    </Box>
  );
}
